import { PublicKey, PublicKeyInitData } from "@solana/web3.js";
import * as SPLToken from "@solana/spl-token";
import * as MPLTokenMetadata from "@metaplex-foundation/mpl-token-metadata";
import * as MPLBubblegum from "./programs/mpl-bubblegum";
import BN from "bn.js";

export class PDA extends PublicKey {
  public readonly bump: number;

  protected constructor(value: PublicKeyInitData, bump: number) {
    super(value);
    this.bump = bump;
  }

  protected static _cache: Map<string, PDA> = new Map();

  /**
   * Finds PDA for given program ID and seeds.
   */
  public static find(programId: PublicKey, ...seeds: Uint8Array[]): PDA {
    const cacheKey = `${programId.toString()}:${seeds.map((seed) =>
      seed.toString(),
    )}`;
    const cached = this._cache.get(cacheKey);
    if (cached) return cached;

    const [publicKey, bump] = PublicKey.findProgramAddressSync(
      seeds,
      programId,
    );
    const result = new PDA(publicKey, bump);
    this._cache.set(cacheKey, result);

    return result;
  }

  /**
   * Returns associated token account address.
   */
  public static token(
    mint: PublicKey,
    owner: PublicKey,
    tokenProgramId?: PublicKey,
  ): PDA {
    tokenProgramId ??= SPLToken.TOKEN_PROGRAM_ID;
    return this.find(
      SPLToken.ASSOCIATED_TOKEN_PROGRAM_ID,
      owner.toBuffer(),
      tokenProgramId.toBuffer(),
      mint.toBuffer(),
    );
  }

  /**
   * Returns Metaplex metadata account address.
   */
  public static tokenMetadata(mint: PublicKey): PDA {
    return this.find(
      MPLTokenMetadata.PROGRAM_ID,
      Buffer.from("metadata", "utf8"),
      MPLTokenMetadata.PROGRAM_ID.toBuffer(),
      mint.toBuffer(),
    );
  }

  /**
   * Returns Metaplex NFT master edition account address.
   */
  public static masterEdition(mint: PublicKey): PDA {
    return this.find(
      MPLTokenMetadata.PROGRAM_ID,
      Buffer.from("metadata", "utf8"),
      MPLTokenMetadata.PROGRAM_ID.toBuffer(),
      mint.toBuffer(),
      Buffer.from("edition", "utf8"),
    );
  }

  /**
   * Returns Metaplex Merkel Tree Authority account address.
   */
  public static merkleTreeAuthority(merkleTree: PublicKey): PDA {
    return this.find(MPLBubblegum.PROGRAM_ID, merkleTree.toBuffer());
  }

  /**
   * Returns CNFT voucher account address.
   */
  public static CNFTVoucher(
    merkleTree: PublicKey,
    leafIndex: number | bigint | BN,
  ): PDA {
    return this.find(
      MPLBubblegum.PROGRAM_ID,
      Buffer.from("voucher", "utf8"),
      merkleTree.toBuffer(),
      bigIntToBuffer(leafIndex),
    );
  }

  /**
   * Returns CNFT asset address.
   */
  public static CNFTAsset(
    merkleTree: PublicKey,
    leafNonce: number | bigint | BN,
  ): PDA {
    return this.find(
      MPLBubblegum.PROGRAM_ID,
      Buffer.from("asset", "utf8"),
      merkleTree.toBuffer(),
      bigIntToBuffer(leafNonce),
    );
  }

  /**
   * Returns CNFT Mint Authority account address.
   */
  public static CNFTMintAuthority(
    merkleTree: PublicKey,
    leafNonce: number | bigint | BN,
  ): PDA {
    return this.find(
      MPLBubblegum.PROGRAM_ID,
      this.CNFTAsset(merkleTree, leafNonce).toBuffer(),
    );
  }

  /**
   * Returns Metaplex Bubblegum Program Signer
   */
  public static bublegumSigner(scope: string): PDA {
    return this.find(MPLBubblegum.PROGRAM_ID, Buffer.from(scope, "utf8"));
  }
}

function bigIntToBuffer(n: number | bigint | BN): Buffer {
  const result = Buffer.alloc(8);
  result.writeBigUInt64LE(BigInt(n.toString()));
  return result;
}
